import * as React from "react";
import { refreshSession } from "../lib/api-client.js";
import { fetchMe } from "./auth-api.js";
import { getAccessToken, getAuthSnapshot, restrictionFrom, setAuthenticated, setUnauthenticated } from "./auth-store.js";

/**
 * Bootstraps the session once on mount, in order: `POST /auth/refresh` exchanges the `HttpOnly`
 * refresh cookie for an access token, then `GET /auth/me` (with that token) loads the profile, and
 * only then is the session `authenticated` — which is what lets the school/campus lists
 * (`InstitutionProvider`) and every page fetch. No session cookie (or an expired one) means the
 * refresh fails and we land on `unauthenticated`, same as an explicit logout.
 *
 * UAT (Phase 22): the bootstrap used to call `/auth/me` with no token and rely on the 401
 * retry-after-refresh — a wasted request, and a window in which other requests raced the refresh.
 * `refreshSession` is shared with `api-client.ts`, so StrictMode's double mount still sends one
 * refresh, and the cancelled first mount never calls `/auth/me`.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  React.useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const refreshed = await refreshSession();
      // A sign-in on the login page while this was in flight owns the session now.
      if (cancelled || getAuthSnapshot().status !== "loading") return;
      const accessToken = getAccessToken();
      if (!refreshed || !accessToken) {
        setUnauthenticated();
        return;
      }
      const me = await fetchMe();
      if (cancelled || getAuthSnapshot().status !== "loading") return;
      setAuthenticated({
        accessToken: getAccessToken() ?? accessToken,
        user: me.user,
        roles: me.roles,
        permissions: me.permissions,
        restriction: restrictionFrom(me),
      });
    }

    bootstrap().catch(() => {
      if (!cancelled && getAuthSnapshot().status === "loading") setUnauthenticated();
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return children;
}
